import { Typography, Badge } from '@lucid/lego';
import type { BadgeProps } from '@lucid/lego';
import type { Order } from '../../../../api/services/lupp/order/dto.order';
import { getMappedOrderStatus, getMappedOrderStatusBadgeColor } from '../orderStatusMapping';
import { isEmbeddedSubventionMethod } from '../Details/PaymentFinancing/constants';
import { formatPrice } from '@lucid/lego';
import { formatDate } from '../../../../utils/tools';

/**
 * Get badge color based on order status
 * @param status - Order status string
 * @returns Badge color variant
 */
export const getStatusBadgeColor = (status?: string | null): BadgeProps['color'] => {
  return getMappedOrderStatusBadgeColor(status);
};

type CreateColumnsParams = {
  theme: 'light' | 'dark';
  onEditClick: (record: Order) => void;
  labels: Record<string, string>;
  locale: string;
};

const formatOrderAmount = (record: Order, amount: number | null | undefined, locale: string) =>
  isEmbeddedSubventionMethod(record.financingDetails?.subventionMethod)
    ? '-'
    : formatPrice(amount, { locale });

/** Order keys excluding object fields, which require a custom renderer */
type OrderScalarKey = Exclude<
  keyof Order,
  'salesLocation' | 'deliveryLocation' | 'financingDetails'
>;

/**
 * Helper to create a simple text column
 */
const createTextColumn = (
  key: OrderScalarKey,
  title: string,
  width: string,
  getValue?: (record: Order) => string
) => ({
  key,
  title,
  width,
  sortable: true,
  render: (_: unknown, record: Order) => (
    <Typography variant="body" size="small" color="black">
      {getValue ? getValue(record) : record[key] || '-'}
    </Typography>
  ),
});

/**
 * Helper to create a clickable link column
 */
const createLinkColumn = (
  key: OrderScalarKey,
  title: string,
  width: string,
  theme: 'light' | 'dark',
  onEditClick: (record: Order) => void,
  getValue?: (record: Order) => string
) => ({
  key,
  title,
  width,
  sortable: true,
  render: (_: unknown, record: Order) => (
    <Typography
      variant="body"
      size="small"
      color="link"
      theme={theme}
      onClick={() => onEditClick(record)}
    >
      {getValue ? getValue(record) : record[key] || '-'}
    </Typography>
  ),
});

export const createColumns = ({ theme, onEditClick, labels, locale }: CreateColumnsParams) => [
  {
    key: 'statusName',
    title: labels?.status || 'Status',
    width: '150px',
    align: 'left' as const,
    sortable: true,
    render: (_: unknown, record: Order) => {
      const mappedStatus = getMappedOrderStatus(record.statusName ?? undefined);

      const statusLabelMap: Record<string, string> = {
        'awaiting assignment':
          labels?.['const.ORDER_STATUS.awaitingAssignment'] ?? 'Awaiting assignment',
        'planning production':
          labels?.['const.ORDER_STATUS.planningProduction'] ?? 'Planning Production',
        'production planned':
          labels?.['const.ORDER_STATUS.productionPlanned'] ?? 'Production Planned',
        'in production': labels?.['const.ORDER_STATUS.inProduction'] ?? 'In Production',
        'factory gated': labels?.['const.ORDER_STATUS.factoryGated'] ?? 'Factory Gated',
        'in transit to eu fpr':
          labels?.['const.ORDER_STATUS.inTransitToEuFpr'] ?? 'In Transit to EU FPR',
        'at eu fpr': labels?.['const.ORDER_STATUS.atEuFpr'] ?? 'At EU FPR',
        dispatched: labels?.['const.ORDER_STATUS.dispatched'] ?? 'Dispatched',
        'on-site': labels?.['const.ORDER_STATUS.onSite'] ?? 'On-site',
        delivered: labels?.['const.ORDER_STATUS.delivered'] ?? 'Delivered',
        cancelled: labels?.['const.ORDER_STATUS.cancelled'] ?? 'Cancelled',
      };
      const statusLabel = statusLabelMap[mappedStatus] ?? 'Awaiting assignment';
      return (
        <Badge
          label={statusLabel}
          color={getStatusBadgeColor(mappedStatus)}
          type="secondary"
          theme={theme === 'dark' ? 'light' : 'dark'}
          minWidth="60px"
        />
      );
    },
  },
  createLinkColumn(
    'orderNumber',
    labels?.orderNumber || 'Order #',
    '150px',
    theme,
    onEditClick,
    (record) => record.orderNumber || '-'
  ),
  createTextColumn('vin', labels?.vin || 'VIN', '160px'),
  createTextColumn('batchNumber', labels?.batch || 'Batch', '120px'),
  createTextColumn('customerType', labels?.customerType || 'Customer Type', '140px'),
  createTextColumn('invoiceToCompanyName', labels?.companyName || 'Company Name', '180px'),
  createLinkColumn(
    'registerToCustomerFullName',
    labels?.fullName || 'Full Name',
    '160px',
    theme,
    onEditClick
  ),
  {
    key: 'salesLocation',
    title: labels?.salesLocation || 'Sales Location',
    width: '150px',
    sortable: true,
    render: (_: unknown, record: Order) => (
      <Typography variant="body" size="small" color="black">
        {record.salesLocation?.locationName || '-'}
      </Typography>
    ),
  },
  {
    key: 'deliveryLocation',
    title: labels?.deliveryLocation || 'Delivery Location',
    width: '150px',
    sortable: true,
    render: (_: unknown, record: Order) => (
      <Typography variant="body" size="small" color="black">
        {record.deliveryLocation?.locationName || '-'}
      </Typography>
    ),
  },
  {
    key: 'customerReadinessDate',
    title: labels?.['customer.deliveryTimeline'] || 'Customer Delivery Timeline',
    width: '180px',
    sortable: true,
    render: (_: unknown, record: Order) => (
      <Typography variant="body" size="small" color="black">
        {formatDate(record.customerReadinessDate)}
      </Typography>
    ),
  },
  {
    key: 'vehicleEtaAtDeliveryCenter',
    title: labels?.vehicleEtaAtDeliveryCenter || 'Vehicle ETA at Delivery Center',
    width: '220px',
    sortable: true,
    render: (_: unknown, record: Order) => (
      <Typography variant="body" size="small" color="black">
        {formatDate(record.vehicleEtaAtDeliveryCenter)}
      </Typography>
    ),
  },
  {
    key: 'scheduledDeliveryDate',
    title: labels?.scheduledDeliveryDate || 'Scheduled Delivery Date',
    width: '180px',
    sortable: true,
    render: (_: unknown, record: Order) => (
      <Typography variant="body" size="small" color="black">
        {formatDate(record.scheduledDeliveryDate)}
      </Typography>
    ),
  },
  {
    key: 'orderTotal',
    title: labels?.orderTotal || 'Order Total',
    width: '140px',
    sortable: true,
    render: (_: unknown, record: Order) => (
      <Typography variant="body" size="small" color="black">
        {formatOrderAmount(record, record.orderTotal, locale)}
      </Typography>
    ),
  },
  {
    key: 'totalAmountReceived',
    title: labels?.totalAmountReceived || 'Total Amount Received',
    width: '180px',
    sortable: true,
    render: (_: unknown, record: Order) => (
      <Typography variant="body" size="small" color="black">
        {formatOrderAmount(record, record.totalAmountReceived, locale)}
      </Typography>
    ),
  },
  {
    key: 'totalAmountDue',
    title: labels?.amountDue || 'Amount Due (Prior to Delivery)',
    width: '200px',
    sortable: true,
    render: (_: unknown, record: Order) => (
      <Typography variant="body" size="small" color="black">
        {formatOrderAmount(record, record.totalAmountDue, locale)}
      </Typography>
    ),
  },
  createTextColumn(
    'assignedOwnerName',
    labels?.assignedOwner || 'Assigned Owner (Agent Name)',
    '160px'
  ),
];
